/**
 * Utility functions for parsing markdown directory structures and converting them to collapsible tree format
 */

export interface DirectoryItem {
  id: string
  name: string
  type: 'directory' | 'file'
  description?: string
  level: number
  children?: DirectoryItem[]
  isExpanded?: boolean
}

/**
 * Extract directory content from markdown that contains MarkdownDirectory components
 * @param markdownContent - The markdown content containing MarkdownDirectory components
 * @returns Array of extracted directory structures
 */
export function extractDirectoryStructures(markdownContent: string): Array<{ title: string; content: string }> {
  const structures: Array<{ title: string; content: string }> = []
  
  // Regular expression to match MarkdownDirectory components
  const regex = /<MarkdownDirectory\s+title="([^"]+)"[^>]*>([\s\S]*?)<\/MarkdownDirectory>/g
  
  let match
  while ((match = regex.exec(markdownContent)) !== null) {
    const [, title, content] = match
    structures.push({
      title: title.trim(),
      content: content.trim()
    })
  }
  
  return structures
}

/**
 * Parse a markdown directory structure and convert it to a tree format
 * @param markdownContent - The markdown content containing directory structure
 * @returns Array of DirectoryItem objects
 */
export function parseMarkdownDirectory(markdownContent: string): DirectoryItem[] {
  try {
    const lines = markdownContent.split('\n')
    
    const tree: DirectoryItem[] = []
    const stack: DirectoryItem[] = []

    for (const line of lines) {
      try {
        // Skip empty lines
        if (!line.trim()) {
          continue
        }

        // Parse directory structure line - handle both emoji and tree drawing formats
        let match = line.match(/^(\s*)(📁|📄)\s*(.+?)(?:\s+#\s*(.+))?$/)
        let level = 0
        let name = ''
        let description = ''
        let isDirectory = false

        if (match) {
          // Emoji format: 📁 src/ # Source code directory
          [, , , name, description] = match
          isDirectory = true
          level = Math.floor((line.length - line.trimStart().length) / 2) // Calculate level from indentation
        } else {
          // Tree drawing format - handle multiple patterns
          
          // Pattern 1: Root directory (no leading characters, ends with /)
          match = line.match(/^([^├└│\s]*\/?)\s*(?:\s+#\s*(.+))?$/)
          if (match && !line.includes('├──') && !line.includes('└──') && !line.includes('│')) {
            const [, extractedName, extractedDescription] = match
            name = extractedName
            description = extractedDescription
            isDirectory = name.endsWith('/')
            level = 0
          } else {
            // Pattern 2: Standard tree lines with ├── or └──
            match = line.match(/^(\s*[│\s]*)(├──|└──)\s*([^#\n]+?)(?:\s+#\s*(.+))?$/)
            if (match) {
              const [, leadingChars, , extractedName, extractedDescription] = match
              
              // Assign to the outer scope variables
              name = extractedName
              description = extractedDescription
              
              // Check if it's a directory based on name ending with / or description containing directory/folder
              isDirectory = Boolean(name.endsWith('/') || name.includes('/') || 
                           (description && (description.includes('directory') || description.includes('folder'))))
              
              // Calculate level based on the structure of leading characters
              // Count the number of │ characters to determine the nesting level
              let pipeCount = 0
              
              for (let i = 0; i < leadingChars.length; i++) {
                const char = leadingChars[i]
                if (char === '│') {
                  pipeCount++
                }
              }
              
              // Level is determined by the number of │ characters
              // Each │ represents one level of nesting
              // Root items (├── or └──) start at level 1
              level = pipeCount + 1
            } else {
              // Pattern 3: Lines that start with │ (continuation lines)
              if (line.trim().startsWith('│')) {
                continue
              } else {
                continue
              }
            }
          }
        }
        
        // Create directory item
        const item: DirectoryItem = {
          id: generateId(name, level),
          name: name.trim(),
          type: isDirectory ? 'directory' : 'file',
          description: description?.trim(),
          level,
          children: [],
          isExpanded: false
        }



        // Add to appropriate parent
        if (level === 0) {
          tree.push(item)
          stack.length = 0
          stack.push(item)
        } else {
          // Find parent at the correct level
          while (stack.length > 0 && stack[stack.length - 1].level >= level) {
            stack.pop()
          }
          
          if (stack.length > 0) {
            const parent = stack[stack.length - 1]
            if (!parent.children) parent.children = []
            parent.children.push(item)
          } else {
            // Fallback: add to root level
            tree.push(item)
          }
          
          stack.push(item)
        }
        
      } catch {
        continue
      }
    }

    return tree
  } catch {
    return []
  }
}

/**
 * Parse a simple text-based directory structure
 * @param text - The text content containing directory structure
 * @returns Array of DirectoryItem objects
 */
export function parseTextDirectory(text: string): DirectoryItem[] {
  const lines = text.split('\n')
  const tree: DirectoryItem[] = []
  const stack: DirectoryItem[] = []

  for (const line of lines) {
    if (!line.trim()) continue

    // Count leading spaces to determine level
    const leadingSpaces = line.match(/^(\s*)/)?.[1].length || 0
    const level = Math.floor(leadingSpaces / 2) // Assuming 2 spaces per level
    
    // Extract name and description
    const content = line.trim()
    const [name, ...descParts] = content.split(' # ')
    const description = descParts.length > 0 ? descParts.join(' # ') : undefined

    // Determine if it's a directory or file
    const isDirectory = name.endsWith('/') || name.includes('/') || 
                       (description && (description.includes('directory') || description.includes('folder')))

    const item: DirectoryItem = {
      id: generateId(name, level),
      name: name.replace(/\/$/, ''), // Remove trailing slash
      type: isDirectory ? 'directory' : 'file',
      description,
      level,
      children: [],
      isExpanded: false
    }

    // Add to appropriate parent
    if (level === 0) {
      tree.push(item)
      stack.length = 0
      stack.push(item)
    } else {
      // Find parent at the correct level
      while (stack.length > 0 && stack[stack.length - 1].level >= level) {
        stack.pop()
      }
      
      if (stack.length > 0) {
        const parent = stack[stack.length - 1]
        if (!parent.children) parent.children = []
        parent.children.push(item)
      }
      
      stack.push(item)
    }
  }

  return tree
}

/**
 * Generate a unique ID for a directory item
 * @param name - The name of the item
 * @param level - The nesting level
 * @returns A unique ID string
 */
function generateId(name: string, level: number): string {
  return `${level}-${name.replace(/[^a-zA-Z0-9]/g, '-')}-${Date.now()}`
}

/**
 * Convert a simple object structure to DirectoryItem format
 * @param obj - Object with directory structure
 * @param baseLevel - Starting level for the tree
 * @returns Array of DirectoryItem objects
 */
export function objectToDirectoryTree(obj: any, baseLevel: number = 0): DirectoryItem[] {
  const tree: DirectoryItem[] = []

  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // It's a directory
      const item: DirectoryItem = {
        id: generateId(key, baseLevel),
        name: key,
        type: 'directory',
        level: baseLevel,
        children: objectToDirectoryTree(value, baseLevel + 1)
      }
      tree.push(item)
    } else {
      // It's a file
      const item: DirectoryItem = {
        id: generateId(key, baseLevel),
        name: key,
        type: 'file',
        description: typeof value === 'string' ? value : undefined,
        level: baseLevel
      }
      tree.push(item)
    }
  }

  return tree
}


